/**
 * 폰 「글자 크기」 배율 재기(10-08 way 「휴대폰 글자를 많이 키워 보는 분이 많아 깨져 보인다」).
 *
 * 안드로이드 앱(WebView)·삼성 인터넷은 시스템 글자 배율을 **글자에만** 곱한다 — 칸·여백(px/rem)은 그대로라
 * 2열 타일·한 줄 표가 넘친다. CSS 로는 그 배율을 알 수 없어서 같은 글자를 두 번 잰다:
 * 화면에 그린 폭(배율 적용) ÷ 캔버스 measureText 폭(배율 미적용) = 배율.
 * 결과는 <html data-text="l|xl"> 로 걸어 화면이 칸 수를 줄인다(1.25 이상 l · 1.6 이상 xl).
 */

import { useEffect, useState } from 'react';

const SAMPLE = '가나다라마바사아자차카타파하';

export function measureTextScale(): number {
  if (typeof document === 'undefined' || !document.body) return 1;
  const span = document.createElement('span');
  span.textContent = SAMPLE;
  span.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;font-size:20px;left:-9999px;top:0';
  document.body.appendChild(span);
  const domW = span.getBoundingClientRect().width;
  const family = getComputedStyle(span).fontFamily;
  span.remove();
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx || !domW) return 1;
  ctx.font = `20px ${family}`;
  const baseW = ctx.measureText(SAMPLE).width;
  if (!baseW) return 1;
  const r = domW / baseW;
  // 글꼴 차이로 생기는 ±5% 는 1 로 본다.
  return r < 1.05 ? 1 : Math.round(r * 100) / 100;
}

export function textScaleLevel(scale: number): '' | 'l' | 'xl' {
  return scale >= 1.6 ? 'xl' : scale >= 1.25 ? 'l' : '';
}

/** html[data-text] 를 걸고 배율 단계를 돌려준다. 앱으로 돌아올 때(설정에서 바꾸고 옴)·화면 회전 때 다시 잰다. */
export function useTextScale(): '' | 'l' | 'xl' {
  const [level, setLevel] = useState<'' | 'l' | 'xl'>('');
  useEffect(() => {
    const run = () => {
      const lv = textScaleLevel(measureTextScale());
      if (lv) document.documentElement.dataset.text = lv;
      else delete document.documentElement.dataset.text;
      setLevel(lv);
    };
    run();
    void document.fonts?.ready.then(run);
    const onVis = () => document.visibilityState === 'visible' && run();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('resize', run);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('resize', run);
    };
  }, []);
  return level;
}
